import {
  CurtailmentData,
  ReadingsDataProcessedPoint,
} from "@inrange/building-manager-api-client";
import { AggregationMode, Formatters } from "./types";

/**
 * Aggregation helpers for the processed-readings chart.
 *
 * The chart renders two resolutions from one dataset: raw settlement periods
 * ("none") and daily/weekly/monthly buckets. Anything keyed on a settlement
 * timestamp - curtailment flags, weather actuals, the expected-generation mask -
 * has to be folded into buckets explicitly, because a bucket's x is the *first*
 * settlement timestamp it contains. Looking a midday-only signal up by that
 * timestamp silently returns nothing, which is how the curtailment series used
 * to vanish the moment daily aggregation was selected.
 */

export const getBucketKey = (
  dateStr: string,
  mode: AggregationMode
): string => {
  if (mode === "daily") return dateStr;
  if (mode === "monthly") return dateStr.substring(0, 7);
  // weekly: find Monday of the week
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  const dow = date.getUTCDay(); // 0=Sun
  const daysToMonday = dow === 0 ? 6 : dow - 1;
  date.setUTCDate(date.getUTCDate() - daysToMonday);
  return date.toISOString().substring(0, 10);
};

/** True when the point's own source passes the current source filter. */
export type SourceFilter = (source: string | undefined) => boolean;

export const aggregateByTimePeriod = (
  data: ReadingsDataProcessedPoint[],
  mode: AggregationMode,
  formatters: Formatters,
  shouldIncludeSource: SourceFilter
): ReadingsDataProcessedPoint[] => {
  if (mode === "none" || data.length === 0) return data;

  const buckets = new Map<
    string,
    {
      first: ReadingsDataProcessedPoint;
      demand: number;
      behindMeterPV: number;
      behindMeterBattery: number;
      generation: number;
      import: number;
      export: number;
      exportRaw: number;
      batteryChargeFromPV: number;
      batteryChargeFromGrid: number;
      batteryDischargeGrid: number;
      batteryAuxDemand: number;
      batteryFinalCharge: number;
      batteryFinalChargeSource: string;
      exportMetered: number;
      networkImport: number;
      generationSources: Set<string>;
      importSources: Set<string>;
      exportSources: Set<string>;
    }
  >();

  for (const point of data) {
    const dateStr = formatters.toDateString(point.x);
    const key = getBucketKey(dateStr, mode);
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        first: point,
        demand: 0,
        behindMeterPV: 0,
        behindMeterBattery: 0,
        generation: 0,
        import: 0,
        export: 0,
        exportRaw: 0,
        batteryChargeFromPV: 0,
        batteryChargeFromGrid: 0,
        batteryDischargeGrid: 0,
        batteryAuxDemand: 0,
        batteryFinalCharge: 0,
        batteryFinalChargeSource: "",
        exportMetered: 0,
        networkImport: 0,
        generationSources: new Set(),
        importSources: new Set(),
        exportSources: new Set(),
      };
      buckets.set(key, bucket);
    }

    // Source filtering is applied here, at settlement-period resolution. A
    // bucket's joined source string ("solaredge, mqtt") never matches an entry
    // in the selected-source list, so re-testing the filter downstream would
    // zero out every mixed-source bucket.
    const genIncluded = shouldIncludeSource(point.generationSource);
    const importIncluded = shouldIncludeSource(point.importSource);
    const exportIncluded = shouldIncludeSource(point.exportSource);
    const batteryIncluded = shouldIncludeSource(point.batteryFinalChargeSource);

    bucket.demand += point.demand;
    bucket.behindMeterPV += point.behindMeterPV;
    bucket.behindMeterBattery += point.behindMeterBattery;
    bucket.generation += genIncluded ? point.generation : 0;
    bucket.import += importIncluded ? point.import : 0;
    bucket.export += exportIncluded ? point.export : 0;
    bucket.exportRaw += exportIncluded ? point.exportRaw : 0;
    bucket.batteryChargeFromPV += point.batteryChargeFromPV;
    bucket.batteryChargeFromGrid += point.batteryChargeFromGrid;
    bucket.batteryDischargeGrid += point.batteryDischargeGrid;
    bucket.batteryAuxDemand += point.batteryAuxDemand;
    // Battery charge is a state of charge, not a flow - carry the last
    // included reading in the bucket rather than summing.
    if (batteryIncluded) {
      bucket.batteryFinalCharge = point.batteryFinalCharge;
      bucket.batteryFinalChargeSource = point.batteryFinalChargeSource;
    }
    bucket.exportMetered += point.exportMetered ?? 0;
    bucket.networkImport += point.networkImport ?? 0;

    if (genIncluded && point.generationSource) {
      point.generationSource
        .split(", ")
        .forEach((s) => bucket!.generationSources.add(s));
    }
    if (importIncluded && point.importSource) {
      point.importSource
        .split(", ")
        .forEach((s) => bucket!.importSources.add(s));
    }
    if (exportIncluded && point.exportSource) {
      point.exportSource
        .split(", ")
        .forEach((s) => bucket!.exportSources.add(s));
    }
  }

  const result: ReadingsDataProcessedPoint[] = [];
  for (const bucket of buckets.values()) {
    result.push({
      x: bucket.first.x,
      esysUuid: bucket.first.esysUuid,
      demand: bucket.demand,
      behindMeterPV: bucket.behindMeterPV,
      behindMeterBattery: bucket.behindMeterBattery,
      generation: bucket.generation,
      generationSource: Array.from(bucket.generationSources).join(", "),
      import: bucket.import,
      importSource: Array.from(bucket.importSources).join(", "),
      export: bucket.export,
      exportSource: Array.from(bucket.exportSources).join(", "),
      exportRaw: bucket.exportRaw,
      batteryChargeFromPV: bucket.batteryChargeFromPV,
      batteryChargeFromGrid: bucket.batteryChargeFromGrid,
      batteryDischargeGrid: bucket.batteryDischargeGrid,
      batteryAuxDemand: bucket.batteryAuxDemand,
      batteryFinalCharge: bucket.batteryFinalCharge,
      batteryFinalChargeSource: bucket.batteryFinalChargeSource,
      exportMetered: bucket.exportMetered,
      networkImport: bucket.networkImport,
    });
  }

  return result;
};

/**
 * bucket key -> the chart x-coordinate of the bar representing that bucket.
 *
 * Every settlement-period-keyed overlay resolves through this so its x values
 * land exactly on the bars rather than a few minutes off.
 */
export const buildBucketTsMap = (
  barPoints: ReadingsDataProcessedPoint[],
  mode: AggregationMode,
  formatters: Formatters
): Map<string, number> => {
  const map = new Map<string, number>();
  for (const point of barPoints) {
    const key = getBucketKey(formatters.toDateString(point.x), mode);
    if (!map.has(key)) {
      map.set(key, formatters.shiftToSiteTimezone(point.x));
    }
  }
  return map;
};

/**
 * raw settlement timestamp (UTC epoch ms) -> chart x of the mark it belongs to.
 *
 * In "none" mode that is the point's own shifted timestamp; in aggregated modes
 * it is the bucket's bar. Timestamps absent from the map are outside the
 * visible window and must not be counted.
 */
export const buildRawTsToBarX = (
  rawPoints: ReadingsDataProcessedPoint[],
  mode: AggregationMode,
  formatters: Formatters
): Map<number, number> => {
  const map = new Map<number, number>();
  if (mode === "none") {
    for (const point of rawPoints) {
      map.set(formatters.getTime(point.x), formatters.shiftToSiteTimezone(point.x));
    }
    return map;
  }
  const bucketTs = buildBucketTsMap(rawPoints, mode, formatters);
  for (const point of rawPoints) {
    const key = getBucketKey(formatters.toDateString(point.x), mode);
    const barX = bucketTs.get(key);
    if (barX !== undefined) map.set(formatters.getTime(point.x), barX);
  }
  return map;
};

export interface BucketedCurtailment {
  /** chart x -> number of curtailed settlement periods in that bar */
  countByBarX: Map<number, number>;
  /** chart x -> estimated curtailed kWh summed over that bar */
  estByBarX: Map<number, number>;
  /** curtailed settlement periods across the whole visible window */
  totalCount: number;
  /** estimated curtailed kWh across the whole visible window */
  totalEst: number;
  /**
   * Every curtailed settlement timestamp (UTC epoch ms) in the payload, for PR
   * exclusion. Deliberately NOT narrowed to the visible window: the PR
   * denominator drops curtailed weather-actuals periods, and those are filtered
   * by date range rather than by presence in the readings array.
   */
  curtailedTimestamps: Set<number>;
}

export const bucketCurtailment = (
  curtailmentData: CurtailmentData | undefined,
  rawTsToBarX: Map<number, number>,
  formatters: Formatters
): BucketedCurtailment => {
  const countByBarX = new Map<number, number>();
  const estByBarX = new Map<number, number>();
  const curtailedTimestamps = new Set<number>();
  let totalCount = 0;
  let totalEst = 0;

  if (!curtailmentData) {
    return { countByBarX, estByBarX, totalCount, totalEst, curtailedTimestamps };
  }

  for (let i = 0; i < curtailmentData.x.length; i++) {
    const ts = formatters.getTime(curtailmentData.x[i]);
    if (curtailmentData.isCurtailed[i]) curtailedTimestamps.add(ts);

    const barX = rawTsToBarX.get(ts);
    if (barX === undefined) continue; // outside the visible window

    if (curtailmentData.isCurtailed[i]) {
      countByBarX.set(barX, (countByBarX.get(barX) ?? 0) + 1);
      totalCount++;
    }
    const est = curtailmentData.estimatedCurtailedKwh[i];
    if (est != null && est > 0) {
      estByBarX.set(barX, (estByBarX.get(barX) ?? 0) + est);
      totalEst += est;
    }
  }

  return { countByBarX, estByBarX, totalCount, totalEst, curtailedTimestamps };
};

/**
 * Settlement period length in ms, inferred from consecutive RAW readings
 * (30 min UK, 15 min NLD). Must never be derived from the aggregated array -
 * there consecutive points are a day/week/month apart.
 */
export const inferSettlementPeriodMs = (
  rawPoints: ReadingsDataProcessedPoint[],
  fallbackMs = 30 * 60 * 1000
): number => {
  if (rawPoints.length < 2) return fallbackMs;
  const delta =
    new Date(rawPoints[1].x).getTime() - new Date(rawPoints[0].x).getTime();
  return delta > 0 ? delta : fallbackMs;
};
